import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import {
  formatFinancialYearLabel,
  readStoredFinancialYearStartYear,
  getFinancialYearDropdownOptions,
  FY_MONTH_ORDER_SHORT,
} from "../utils/financialYear";
import { APP_BRANCHES } from "../config/appBranches";
import {
  branchIdFromAppBranch,
  buildPartyMonthlyReport,
  listUniqueParties,
  loadExpenseRowsForFinancialYear,
} from "../features/expenseDataImport/services/expenseReportService";
import { salesImportBranchDisplayName } from "../features/salesDataImport/config/salesImportBranchIds";
import "./MonthlySaleReportPage.css";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function readParams(searchParams) {
  const fyStartYear = Number(searchParams.get("fy")) || readStoredFinancialYearStartYear();
  const party = searchParams.get("party") || "";
  const branch = searchParams.get("branch") || "all";
  return { fyStartYear, party, branchFilter: branch };
}

function queryString(fyStartYear, party, branchFilter) {
  const q = new URLSearchParams({ fy: String(fyStartYear), branch: branchFilter });
  if (party) q.set("party", party);
  return q.toString();
}

export default function ExpensePartyMonthlyReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = readParams(searchParams);

  const [fyStartYear, setFyStartYear] = useState(initial.fyStartYear);
  const [branchFilter, setBranchFilter] = useState(initial.branchFilter);
  const [partyName, setPartyName] = useState(initial.party);
  const [partyInput, setPartyInput] = useState(initial.party);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState("");
  const [selectedMonthSlot, setSelectedMonthSlot] = useState(null);

  const fyLabel = formatFinancialYearLabel(fyStartYear);
  const fySelectOptions = useMemo(() => getFinancialYearDropdownOptions(fyStartYear), [fyStartYear]);
  const branchIdFilter = branchFilter === "all" ? "all" : branchIdFromAppBranch(branchFilter);

  useEffect(() => {
    setSearchParams(queryString(fyStartYear, partyName, branchFilter), { replace: true });
  }, [fyStartYear, partyName, branchFilter, setSearchParams]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setProgress("Loading expense data…");
    try {
      const data = await loadExpenseRowsForFinancialYear(fyStartYear, (done, total) => {
        setProgress(`Loading month ${done} / ${total}…`);
      });
      setRows(data);
    } catch (e) {
      toast.error(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
      setProgress("");
    }
  }, [fyStartYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const parties = useMemo(() => listUniqueParties(rows, branchIdFilter), [rows, branchIdFilter]);

  const report = useMemo(
    () => buildPartyMonthlyReport(rows, partyName, branchIdFilter),
    [rows, partyName, branchIdFilter]
  );

  const monthVouchers = useMemo(() => {
    if (selectedMonthSlot == null) return [];
    return report.vouchers.filter((v) => Number(v.fySlot) === selectedMonthSlot);
  }, [report.vouchers, selectedMonthSlot]);

  const applyParty = () => {
    const trimmed = partyInput.trim();
    setPartyName(trimmed);
    setSelectedMonthSlot(null);
  };

  return (
    <>
      <MenuBar />
      <HeaderBar />

      <main className="msr-upload-page msr-no-print">
        <header className="msr-upload-page-head">
          <div>
            <p className="msr-upload-page-kicker">Reports</p>
            <h1 className="msr-upload-page-title">Expense — party monthly (debit / credit)</h1>
            <p className="msr-upload-page-sub">
              Select a party to see monthly <strong>debit</strong> and <strong>credit</strong> from uploaded expense
              data for FY <strong>{fyLabel}</strong>.
            </p>
          </div>
          <Link to="/reports/expense-upload" className="msr-upload-page-back">
            Expense upload →
          </Link>
        </header>

        <div className="msr-toolbar msr-upload-toolbar">
          <label className="msr-toolbar-label">
            Financial year{" "}
            <select className="msr-toolbar-select" value={fyStartYear} onChange={(e) => setFyStartYear(Number(e.target.value))}>
              {fySelectOptions.map(({ startYear, label }) => (
                <option key={startYear} value={startYear}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label">
            Branch{" "}
            <select className="msr-toolbar-select" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
              <option value="all">All branches</option>
              {APP_BRANCHES.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="msr-toolbar-label flex-1 min-w-[14rem]">
            Party{" "}
            <input
              className="msr-toolbar-select w-full max-w-md"
              list="expense-party-list"
              value={partyInput}
              onChange={(e) => setPartyInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyParty()}
              placeholder="Type or select party name…"
            />
            <datalist id="expense-party-list">
              {parties.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
          </label>
          <button type="button" className="msr-print-btn" onClick={applyParty} disabled={!partyInput.trim()}>
            Show report
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">{progress || "Loading…"}</p>
        ) : !partyName ? (
          <p className="text-sm text-slate-600">
            {parties.length} parties found. Select a party and click <strong>Show report</strong>.
          </p>
        ) : report.months.length === 0 ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            No expense rows for <strong>{partyName}</strong> in FY {fyLabel}
            {branchFilter !== "all" ? ` · ${branchFilter}` : ""}. Upload data on the expense upload page.
          </p>
        ) : (
          <>
            <section className="msr-precalc-panel mb-4">
              <h2 className="msr-precalc-title">{partyName}</h2>
              <p className="msr-precalc-meta">
                FY {fyLabel}
                {branchFilter !== "all" ? ` · ${branchFilter}` : ""} · {report.totals.count} vouchers
              </p>
              <div className="msr-precalc-totals-grid">
                <div className="msr-precalc-total-box">
                  <span>Total debit</span>
                  <strong>{fmt(report.totals.debit)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Total credit</span>
                  <strong>{fmt(report.totals.credit)}</strong>
                </div>
                <div className="msr-precalc-total-box">
                  <span>Net (Dr − Cr)</span>
                  <strong>{fmt(report.totals.net)}</strong>
                </div>
              </div>
            </section>

            <section className="msr-precalc-panel mb-4">
              <h2 className="msr-precalc-title">Month-wise summary</h2>
              <div className="msr-precalc-table-wrap">
                <table className="msr-precalc-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="msr-num">Debit</th>
                      <th className="msr-num">Credit</th>
                      <th className="msr-num">Net (Dr − Cr)</th>
                      <th className="msr-num">Vouchers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FY_MONTH_ORDER_SHORT.map((label, fySlot) => {
                      const m = report.months.find((x) => x.fySlot === fySlot);
                      const debit = m?.debit || 0;
                      const credit = m?.credit || 0;
                      const net = m?.net || 0;
                      const count = m?.count || 0;
                      const active = selectedMonthSlot === fySlot;
                      return (
                        <tr
                          key={label}
                          className={active ? "bg-sky-50" : count ? "cursor-pointer hover:bg-slate-50" : "text-slate-400"}
                          onClick={() => count && setSelectedMonthSlot(fySlot)}
                        >
                          <td>
                            {label}
                            {m?.calendarYear ? ` ${m.calendarYear}` : ""}
                          </td>
                          <td className="msr-num">{count ? fmt(debit) : "—"}</td>
                          <td className="msr-num">{count ? fmt(credit) : "—"}</td>
                          <td className="msr-num">{count ? fmt(net) : "—"}</td>
                          <td className="msr-num">{count || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="font-semibold">FY total</td>
                      <td className="msr-num font-semibold">{fmt(report.totals.debit)}</td>
                      <td className="msr-num font-semibold">{fmt(report.totals.credit)}</td>
                      <td className="msr-num font-semibold">{fmt(report.totals.net)}</td>
                      <td className="msr-num font-semibold">{report.totals.count}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-xs text-slate-500 mt-2">Click a month row to see voucher detail below.</p>
            </section>

            {selectedMonthSlot != null ? (
              <section className="msr-precalc-panel">
                <h2 className="msr-precalc-title">
                  Voucher detail — {FY_MONTH_ORDER_SHORT[selectedMonthSlot]}
                </h2>
                <div className="msr-precalc-table-wrap">
                  <table className="msr-precalc-table text-xs">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Branch</th>
                        <th>Voucher type</th>
                        <th>Voucher no</th>
                        <th>Txn type</th>
                        <th className="msr-num">Debit</th>
                        <th className="msr-num">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthVouchers.map((v, i) => (
                        <tr key={`${v.voucherNo}-${v.tranDate}-${i}`}>
                          <td>{v.tranDate}</td>
                          <td>{v.branchLabel || salesImportBranchDisplayName(v.branchId)}</td>
                          <td>{v.voucherTypeName}</td>
                          <td>{v.voucherNo}</td>
                          <td>{v.transactionType || "—"}</td>
                          <td className="msr-num">{v.debit > 0 ? fmt(v.debit) : "—"}</td>
                          <td className="msr-num">{v.credit > 0 ? fmt(v.credit) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>
    </>
  );
}
